import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@easydent/shared-ui';
import { useAuthStore } from '@/stores/authStore';
import { tryRestoreSession } from '@/lib/apiClient';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import OperatorsPage from '@/pages/OperatorsPage';
import DashboardPage from '@/pages/DashboardPage';
import TenantsPage from '@/pages/TenantsPage';
import TenantDetailPage from '@/pages/TenantDetailPage';
import PlansPage from '@/pages/PlansPage';
import SuppliersPage from '@/pages/SuppliersPage';
import CreativesPage from '@/pages/CreativesPage';
import BillingPage from '@/pages/BillingPage';
import DemandPage from '@/pages/DemandPage';
import SettingsPage from '@/pages/SettingsPage';

const RequireOperator: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, role } = useAuthStore();
  const location = useLocation();
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  if (role !== 'SuperAdmin') return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const App: React.FC = () => {
  const [booting, setBooting] = useState(true);
  useEffect(() => { tryRestoreSession().finally(() => setBooting(false)); }, []);

  if (booting) {
    return <div className="flex h-screen items-center justify-center"><Spinner size="lg" /></div>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Both are reached by someone who cannot sign in, so neither may sit behind RequireOperator. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<RequireOperator><DashboardPage /></RequireOperator>} />
      <Route path="/tenants" element={<RequireOperator><TenantsPage /></RequireOperator>} />
      <Route path="/tenants/:id" element={<RequireOperator><TenantDetailPage /></RequireOperator>} />
      <Route path="/plans" element={<RequireOperator><PlansPage /></RequireOperator>} />
      <Route path="/suppliers" element={<RequireOperator><SuppliersPage /></RequireOperator>} />
      <Route path="/creatives" element={<RequireOperator><CreativesPage /></RequireOperator>} />
      <Route path="/billing" element={<RequireOperator><BillingPage /></RequireOperator>} />
      <Route path="/demand" element={<RequireOperator><DemandPage /></RequireOperator>} />
      <Route path="/operators" element={<RequireOperator><OperatorsPage /></RequireOperator>} />
      <Route path="/settings" element={<RequireOperator><SettingsPage /></RequireOperator>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
